import { Moment } from 'moment';
import { CreditCardBrandEnum } from '../../invoice/PaymentMethod/PaymentMethod.types';
import {
  InvoiceNFeRecipientFormValues,
  RecipientType,
} from '../../invoice/invoice/Invoice.types';
import {
  TaxCouponItemCofinsCst,
  TaxCouponItemIcmsCst,
  TaxCouponItemIcmsOrigin,
  TaxCouponItemIssqn,
  TaxCouponItemPisCst,
} from './coupons.item.types';

export enum TaxCouponPaymentType {
  CASH = 'CASH',
  CHECK = 'CHECK',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  STORE_CREDIT = 'STORE_CREDIT',
  FOOD_VOUCHER = 'FOOD_VOUCHER',
  MEAL_VOUCHER = 'MEAL_VOUCHER',
  GIFT_VOUCHER = 'GIFT_VOUCHER',
  GAS_VOUCHER = 'GAS_VOUCHER',
  TRADE_DUPLICATES = 'TRADE_DUPLICATES',
  BANK_SLIP = 'BANK_SLIP',
  BANK_DEPOSIT = 'BANK_DEPOSIT',
  BANK_INSTANT_TRANSFER = 'BANK_INSTANT_TRANSFER',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASHBACK_CREDIT = 'CASHBACK_CREDIT',
  MULTI_ISSUER_CREDIT_CARD = 'MULTI_ISSUER_CREDIT_CARD',
  NO_PAYMENT = 'NO_PAYMENT',
  OTHERS = 'OTHERS',
}

export const TaxCouponPaymentTypeLabel: Record<TaxCouponPaymentType, string> = {
  [TaxCouponPaymentType.CASH]: 'Dinheiro',
  [TaxCouponPaymentType.CHECK]: 'Cheque',
  [TaxCouponPaymentType.CREDIT_CARD]: 'Cartão de Crédito',
  [TaxCouponPaymentType.DEBIT_CARD]: 'Cartão de Débito',
  [TaxCouponPaymentType.STORE_CREDIT]: 'Crédito da Loja',
  [TaxCouponPaymentType.FOOD_VOUCHER]: 'vale Alimentação',
  [TaxCouponPaymentType.MEAL_VOUCHER]: 'Vale Refeição',
  [TaxCouponPaymentType.GIFT_VOUCHER]: 'Voucher de presente',
  [TaxCouponPaymentType.GAS_VOUCHER]: 'Vale Combustível',
  [TaxCouponPaymentType.TRADE_DUPLICATES]: 'Duplicados comerciais',
  [TaxCouponPaymentType.BANK_SLIP]: 'Boleto Bancário',
  [TaxCouponPaymentType.BANK_DEPOSIT]: 'Depósito Bancário',
  [TaxCouponPaymentType.BANK_INSTANT_TRANSFER]:
    'Transferência Bancária Instantânea',
  [TaxCouponPaymentType.BANK_TRANSFER]: 'Transferência Bancária',
  [TaxCouponPaymentType.CASHBACK_CREDIT]: 'Crédito de Reembolso',
  [TaxCouponPaymentType.MULTI_ISSUER_CREDIT_CARD]:
    'Cartão de Crédito Multi Emissor',
  [TaxCouponPaymentType.NO_PAYMENT]: 'Sem Pagamento',
  [TaxCouponPaymentType.OTHERS]: 'Outros',
};

export interface TaxCouponPayment {
  id: string;
  paymentType: TaxCouponPaymentType;
  value: number;
  acquirer?: { type: CreditCardBrandEnum; authorizationCode: string };
}

export interface TaxCouponCustomer {
  id: number;
  cpfCnpj: string;
  clientName: string;
  deliveryAddress?: TaxCouponCustomerDelivery;
}

export interface TaxCouponCustomerDelivery {
  street: string;
  streetNumber: string;
  complement?: string;
  district: string;
  city: string;
  cityId: number;
  state: string;
  zipcode: string;
  country: string;
}

export enum TaxCouponItemType {
  TASK = 'TASK',
  PART = 'PART',
}

export const TaxCouponItemTypeColor = {
  [TaxCouponItemType.PART]: 'blue',
  [TaxCouponItemType.TASK]: 'yellow',
};

export const TaxCouponItemTypeLabel = {
  [TaxCouponItemType.PART]: 'Peça',
  [TaxCouponItemType.TASK]: 'Serviço',
};

export interface TaxCouponItem {
  id: string;
  itemId: number;
  description: string;
  additionalInformation?: string;
  unitMeasure?: string;
  quantity: number;
  unitValue: number;
  calculationRule?: string;
  discount?: number;
  expenses?: number;
  fuel?: {
    anpCode?: string;
    simpCode?: string;
  };
  type: TaxCouponItemType;
  taxes?: {
    icms?: {
      origin: TaxCouponItemIcmsOrigin;
      cst: TaxCouponItemIcmsCst;
      rate: number;
      csosn: string;
    };
    pis?: {
      cst: TaxCouponItemPisCst;
      baseCalculation: number;
      rate: number;
    };
    issqn?: {
      rate: number;
      deduction: number;
      taxType: TaxCouponItemIssqn;
      taxIncentive: boolean;
    };
    cofins?: {
      cst: TaxCouponItemCofinsCst;
      baseCalculation: number;
      rate: number;
    };
    ncmId?: number;
    cfopId?: number;
  };
}

export enum CouponOriginEntity {
  WORK_ORDER = 'WORK_ORDER',
  SALES = 'SALES',
  PURCHASE = 'PURCHASE',
}

export enum CouponOriginType {
  PRIVATE = 'PRIVATE',
  INSURANCE_DEDUCTIBLE = 'INSURANCE_DEDUCTIBLE',
  INSURANCE = 'INSURANCE',
}

export interface CouponOrigin {
  entity: CouponOriginEntity;
  entityCode: string;
  entityId: string;
  entityType?: CouponOriginType;
}

export interface TaxCoupon {
  id: string;
  status: TaxCouponStatus;
  sefazReferenceCode?: string;
  sefazBreakdown?: {
    UF: string;
    emissionDate: string;
    cnpj: string;
    model: string;
    serie: string;
    cfeNumber: string;
    randomNumber: string;
    verificationCode: string;
  };
  origins?: CouponOrigin[];
  recipient?: TaxCouponCustomer;
  couponItems: TaxCouponItem[];
  payments: TaxCouponPayment[];
  createdAt: string;
}

export interface TaxCouponListResponse {
  data: TaxCoupon[];
  pagination: {
    page: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

export interface TaxCouponGeneralForm {
  id: string;
  companyId: number;
  status: TaxCouponStatus;
  customer?: TaxCouponCustomer;
  createdAt: Moment;
  sefazReferenceCode?: string;
}

export enum TaxCouponStatus {
  TYPING = 'TYPING',
  IN_PROGRESS = 'IN_PROGRESS',
  AUTHORIZED = 'AUTHORIZED',
  EMITTED = 'EMITTED',
  CANCELED = 'CANCELED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  REJECTED = 'REJECTED',
}

export const TaxCouponStatusLabel: Partial<Record<TaxCouponStatus, string>> = {
  [TaxCouponStatus.AUTHORIZED]: 'AUTORIZADO',
  [TaxCouponStatus.CANCELED]: 'CANCELADO',
  [TaxCouponStatus.EMITTED]: 'EMITIDO',
  [TaxCouponStatus.ERROR]: 'ERRO',
  [TaxCouponStatus.IN_PROGRESS]: 'EM PROGRESSO',
  [TaxCouponStatus.REJECTED]: 'REJEITADO',
  [TaxCouponStatus.SUCCESS]: 'SUCESSO',
  [TaxCouponStatus.TYPING]: 'EM DIGITAÇÃO',
};

export const TaxCouponStatusColor: Partial<Record<TaxCouponStatus, string>> = {
  [TaxCouponStatus.AUTHORIZED]: 'blue',
  [TaxCouponStatus.CANCELED]: 'red',
  [TaxCouponStatus.EMITTED]: 'blue',
  [TaxCouponStatus.ERROR]: 'red',
  [TaxCouponStatus.IN_PROGRESS]: 'blue',
  [TaxCouponStatus.REJECTED]: 'red',
  [TaxCouponStatus.SUCCESS]: 'green',
  [TaxCouponStatus.TYPING]: 'gray',
};

export interface TaxCouponDetail {
  id: string;
  status: TaxCouponStatus;
  prevStatus: [];
  createdAt: string;
  recipient: {
    cpfCnpj: string;
    clientName: string;
    deliveryAddress: null | TaxCouponCustomerDelivery;
  };
  couponItems: [];
  listPayment: [];
}

export interface TaxCouponRecipientFormValues {
  cpfCnpj: string;
  recipientType: RecipientType;
  customerId?: number;
  customer?: string;
  insurerId?: number;
  insurer?: string;
  supplierId?: number;
  supplier?: string;
  deliveryAddress?: {
    zipcode: string;
    complement?: string;
    street: string;
    streetNumber: string;
    district: string;
    city: string;
    cityId: number;
    state: string;
    country: string;
  };
}

export interface TaxCouponDetailRecipientFormValues {
  id?: number;
  recipientName?: string;
  cpfCnpj?: string;
  hasAddress: boolean;

  recipient?: InvoiceNFeRecipientFormValues;

  deliveryAddress?: {
    street: string;
    streetNumber: string;
    complement?: string;
    district: string;
    city: string;
    cityId: number;
    state: string;
    zipcode: string;
  };
}

export interface TaxCouponFormValues {
  origins?: CouponOrigin[];
  recipient: TaxCouponRecipientFormValues;
  couponItems: [];
  payments: Omit<TaxCouponPayment, 'id'>[];
  hasCustomerAddress: boolean;
  companyId: number;
}

export interface TaxCouponCreateRequest {
  companyId: number;
  origins?: CouponOrigin[];
  recipient: TaxCouponCustomer;
  couponItems?: Omit<TaxCouponItem, 'id'>[];
  payments?: Omit<TaxCouponPayment, 'id'>[];
  createdAt?: string;
}

export interface TaxCouponDetailRequest {
  taxCouponId?: string;
}

export interface TaxCouponListRequest {
  companyId?: number;
  status?: TaxCouponStatus;
  page?: number;
  pageSize?: number;
  entityCode?: string[];
  entityId?: string[];
  entityType?: CouponOriginType;
  entity?: CouponOriginEntity;
}

export interface TaxCouponPaymentCreateRequest {
  taxCouponId: string;
  payments: TaxCouponPayment[];
}

export interface TaxCouponPaymentEditRequest {
  taxCouponId: string;
  id: string;
  paymentType: TaxCouponPaymentType;
  value: number;
  acquirer?: { type: CreditCardBrandEnum; authorizationCode: string };
}

export interface TaxCouponPaymentRemoveRequest {
  taxCouponId: string;
  taxCouponPaymentId: string;
}

export interface TaxCouponItemCreateRequest {
  taxCouponId: string;
  couponItems: TaxCouponItem[];
}

export interface TaxCouponItemEditRequest extends TaxCouponItem {
  taxCouponId: string;
}

export interface TaxCouponItemRemoveRequest {
  taxCouponId: string;
  taxCouponItemId: string;
}

export interface TaxCouponRecipientCreateRequest {
  taxCouponId: string;
  id: number;
  cpfCnpj: string;
  clientName: string;
  deliveryAddress?: {
    street: string;
    streetNumber: string;
    complement?: string;
    zipcode: string;
    district: string;
    city: string;
    cityId: number;
    state: string;
    country: string;
  };
}

export interface TaxCouponRecipientCreateResponse {
  id: number;
  clientName: string;
  cpfCnpj: string;
  deliveryAddress: TaxCouponCustomerDelivery;
}

export interface TaxCouponGenerateTx2Request {
  couponId: string;
  equipmentId: string;
}

export interface TaxCouponUpdadeCodeRequest {
  couponId: string;
  sefazReferenceCode: string;
}

export interface CouponWorkorderItem {
  id: number;
  type: string;
  unitPrice: number;
  quantity: number;
  valueDiscount?: number;
  description: string;
}

export interface CouponWorkorderCreate {
  companyId: number;
  origins: CouponOrigin[];
  items: CouponWorkorderItem[];
  recipient: TaxCouponCustomer;
}

export enum CardAccreditoEnum {
  SICREDI_001 = 'SICREDI_001',
  SICREDI_RS_002 = 'SICREDI_RS_002',
  AMEX_003 = 'AMEX_003',
  BANCO_GE_004 = 'BANCO_GE_004',
  BANCO_SAFRA_005 = 'BANCO_SAFRA_005',
  BANCO_TOPAZIO_006 = 'BANCO_TOPAZIO_006',
  BANCO_TRIANGULO_007 = 'BANCO_TRIANGULO_007',
  BIGCARD_008 = 'BIGCARD_008',
  BOURBON_009 = 'BOURBON_009',
  CABAL_010 = 'CABAL_010',
  CETELEM_011 = 'CETELEM_011',
  CIELO_012 = 'CIELO_012',
  CREDI_21_013 = 'CREDI_21_013',
  ECX_CARD_014 = 'ECX_CARD_014',
  EMBRATEC_015 = 'EMBRATEC_015',
  EMPORIO_CARD_016 = 'EMPORIO_CARD_016',
  FREEDDOM_017 = 'FREEDDOM_017',
  FUNCIONAL_CARD_018 = 'FUNCIONAL_CARD_018',
  HIPERCARD_019 = 'HIPERCARD_019',
  MAPA_020 = 'MAPA_020',
  NOVO_PAG_021 = 'NOVO_PAG_021',
  PERNAMBUCANAS_022 = 'PERNAMBUCANAS_022',
  POLICARD_023 = 'POLICARD_023',
  PROVAR_024 = 'PROVAR_024',
  REDECARD_025 = 'REDECARD_025',
  RENNER_026 = 'RENNER_026',
  RP_CONVENIOS_027 = 'RP_CONVENIOS_027',
  SANTINVEST_028 = 'SANTINVEST_028',
  SODEXHO_029 = 'SODEXHO_029',
  SOROCRED_030 = 'SOROCRED_030',
  TECBAN_031 = 'TECBAN_031',
  TICKET_032 = 'TICKET_032',
  TRIVALE_033 = 'TRIVALE_033',
  TRICARD_034 = 'TRICARD_034',
  RESERVADO_035_998 = 'RESERVADO_035_998',
  OUTROS_999 = 'OUTROS_999',
}

export const CardAccreditoLabel = {
  [CardAccreditoEnum.SICREDI_001]: 'Administradora de Cartões Sicredi Ltda.',
  [CardAccreditoEnum.SICREDI_RS_002]:
    'Administradora de Cartões Sicredi Ltda. (filial RS)',
  [CardAccreditoEnum.AMEX_003]: 'Banco American Express S/A - AMEX',
  [CardAccreditoEnum.BANCO_GE_004]: 'BANCO GE - CAPITAL',
  [CardAccreditoEnum.BANCO_SAFRA_005]: 'BANCO SAFRA S/A',
  [CardAccreditoEnum.BANCO_TOPAZIO_006]: 'BANCO TOPÁZIO S/A',
  [CardAccreditoEnum.BANCO_TRIANGULO_007]: 'BANCO TRIANGULO S/A',
  [CardAccreditoEnum.BIGCARD_008]: 'BIGCARD Adm. de Convenios e Serv.',
  [CardAccreditoEnum.BOURBON_009]: 'BOURBON Adm. de Cartões de Crédito',
  [CardAccreditoEnum.CABAL_010]: 'CABAL Brasil Ltda.',
  [CardAccreditoEnum.CETELEM_011]: 'CETELEM Brasil S/A - CFI',
  [CardAccreditoEnum.CIELO_012]: 'CIELO S/A',
  [CardAccreditoEnum.CREDI_21_013]: 'CREDI 21 Participações Ltda.',
  [CardAccreditoEnum.ECX_CARD_014]:
    'ECX CARD Adm. e Processadora de Cartões S/A',
  [CardAccreditoEnum.EMBRATEC_015]:
    'Empresa Bras. Tec. Adm. Conv. Hom. Ltda. - EMBRATEC',
  [CardAccreditoEnum.EMPORIO_CARD_016]: 'EMPÓRIO CARD LTDA',
  [CardAccreditoEnum.FREEDDOM_017]: 'FREEDDOM e Tecnologia e Serviços S/A',
  [CardAccreditoEnum.FUNCIONAL_CARD_018]: 'FUNCIONAL CARD LTDA.',
  [CardAccreditoEnum.HIPERCARD_019]: 'HIPERCARD Banco Multiplo S/A',
  [CardAccreditoEnum.MAPA_020]: 'MAPA Admin. Conv. e Cartões Ltda.',
  [CardAccreditoEnum.NOVO_PAG_021]:
    'Novo Pag Adm. e Proc. de Meios Eletrônicos de Pagto. Ltda.',
  [CardAccreditoEnum.PERNAMBUCANAS_022]:
    'PERNAMBUCANAS Financiadora S/A Crédito, Fin. e Invest.',
  [CardAccreditoEnum.POLICARD_023]: 'POLICARD Systems e Serviços Ltda.',
  [CardAccreditoEnum.PROVAR_024]: 'PROVAR Negócios de Varejo Ltda.',
  [CardAccreditoEnum.REDECARD_025]: 'REDECARD S/A',
  [CardAccreditoEnum.RENNER_026]: 'RENNER Adm. Cartões de Crédito Ltda.',
  [CardAccreditoEnum.RP_CONVENIOS_027]: 'RP Administração de Convênios Ltda.',
  [CardAccreditoEnum.SANTINVEST_028]:
    'SANTINVEST S/A Crédito, Financiamento e Investimentos',
  [CardAccreditoEnum.SODEXHO_029]:
    'SODEXHO Pass do Brasil Serviços e Comércio S/A',
  [CardAccreditoEnum.SOROCRED_030]: 'SOROCRED Meios de Pagamentos Ltda.',
  [CardAccreditoEnum.TECBAN_031]: 'Tecnologia Bancária S/A - TECBAN',
  [CardAccreditoEnum.TICKET_032]: 'TICKET Serviços S/A',
  [CardAccreditoEnum.TRIVALE_033]: 'TRIVALE Administração Ltda.',
  [CardAccreditoEnum.TRICARD_034]: 'Unicard Banco Múltiplo S/A - TRICARD',
  [CardAccreditoEnum.RESERVADO_035_998]: 'Reservado',
  [CardAccreditoEnum.OUTROS_999]: 'Outros',
};
